/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { BGR_SWIZZLE_SHADER, Solids } from '@sick-davinci/glow';
import { TileObject3D } from './Tile';
import { DataTexture, DoubleSide, ShaderMaterial, Vector3 } from 'three';
/**
 * Distribute image-data across given tiles
 */
export class TileGridRenderer {
    constructor(_imageTypeConfigurationFactory) {
        this._imageTypeConfigurationFactory = _imageTypeConfigurationFactory;
    }
    renderToObject3D(object, tileGrid, imageData) {
        const existingTileObjects = object.children;
        existingTileObjects.forEach((tileObject) => {
            tileObject.material.uniforms.texture.value = null;
            tileObject.visible = false;
        });
        for (let tileIt = 0; tileIt < tileGrid.length; tileIt++) {
            const tile = tileGrid[tileIt];
            let tileObject = existingTileObjects[tileIt];
            if (!tileObject) {
                tileObject = this._createTileObject(tile);
                object.add(tileObject);
            }
            const texture = this._createTileObjectTexture(tile, imageData);
            texture.needsUpdate = true;
            tileObject.material.uniforms.texture.value = texture;
            tileObject.visible = true;
            tileObject.textureType = imageData.ImageType;
            const oldTileDimension = tileObject.geometry.parameters;
            const newTileDimension = tile.dimension;
            if (this._hasTileDimensionChanged(oldTileDimension, newTileDimension)) {
                tileObject.geometry.dispose();
                tileObject.geometry = this._createTileObjectGeometry(tile);
            }
            const position = this._calculateTileObjectPosition(tile, imageData);
            tileObject.position.set(position.x, position.y, position.z);
        }
    }
    _getTextureFormat(imageType) {
        return this._imageTypeConfigurationFactory.createForType(imageType).textureFormat;
    }
    _createTileObjectTexture(tile, imageData) {
        return new DataTexture(tile.data, tile.dimension.width, tile.dimension.height, this._getTextureFormat(imageData.ImageType));
    }
    _createTileObjectShaderUniforms(texture) {
        return {
            texture: { value: texture || null },
        };
    }
    _createTileObjectMaterial(uniforms) {
        return new ShaderMaterial({
            uniforms,
            vertexShader: BGR_SWIZZLE_SHADER.vertexShader,
            fragmentShader: BGR_SWIZZLE_SHADER.fragmentShader,
            depthWrite: false,
            side: DoubleSide,
        });
    }
    _createTileObjectGeometry(tile) {
        return Solids.createRectangle(tile.dimension.width, tile.dimension.height);
    }
    _createTileObject(tile) {
        const uniforms = this._createTileObjectShaderUniforms();
        const material = this._createTileObjectMaterial(uniforms);
        const geometry = this._createTileObjectGeometry(tile);
        return new TileObject3D(geometry, material);
    }
    _hasTileDimensionChanged(oldDimension, newDimension) {
        return oldDimension.height !== newDimension.height || oldDimension.width !== newDimension.width;
    }
    _calculateTileObjectPosition(tile, imageData) {
        const xPosDiff = (imageData.Width - tile.dimension.width) / 2;
        const yPosDiff = (imageData.Height - tile.dimension.height) / 2;
        const xPos = tile.position.x - xPosDiff;
        const yPos = tile.position.y - yPosDiff;
        return new Vector3(xPos, yPos, 0);
    }
}
//# sourceMappingURL=TileGridRenderer.js.map